import { useCallback, useState } from 'react';
import { z } from 'zod';

import { rage } from '~/rage';
import { Button } from '../../components/Button';
import {
	ButtonContainer,
	FormContainer,
	PanelContainer,
} from '../../components/Containers';
import { FormTitle } from '../../components/FormTitle';
import { TextInput } from '../../components/TextInput';
import { useLayoutDispatch, useLayoutSelector } from '../../redux/hooks';
import { AuthTabs, setActiveTab } from '../../redux/slices/authTabsSlice';
import { IFormError, setLoading } from '../../redux/slices/formDataSlice';

const accountPasswordSchema = z
	.string()
	.min(1, 'Пароль обязателен для заполнения')
	.min(6, 'Пароль должен содержать минимум 6 символов')
	.max(64, 'Пароль не должен превышать 64 символа');

const restoreNewPasswordSchema = z
	.object({
		password: accountPasswordSchema,
		passwordRep: z.string().min(1, 'Повторите пароль'),
	})
	.refine((data) => data.password === data.passwordRep, {
		message: 'Пароли не совпадают',
		path: ['passwordRep'],
	});

const RestoreNewPassword = () => {
	const [password, setPassword] = useState('');
	const [passwordRep, setPasswordRep] = useState('');
	const [errors, setErrors] = useState<IFormError[]>([]);

	const loading = useLayoutSelector((state) => state.formData.loading);
	const email = useLayoutSelector((state) => state.formData.email);
	const resetToken = useLayoutSelector((state) => state.formData.resetToken);
	const dispatch = useLayoutDispatch();

	const handleSubmit = async () => {
		dispatch(setLoading(true));

		const errors: IFormError[] = [];
		try {
			await restoreNewPasswordSchema.parseAsync({
				password,
				passwordRep,
			});

			await rage.accounts.resetPassword(email, resetToken, password);

			rage.notifications.showSuccess('Пароль успешно изменён!');
			dispatch(setActiveTab(AuthTabs.Login));
		} catch (error) {
			if (error instanceof z.ZodError) {
				error.issues.forEach((err) => {
					if (err.path && typeof err.path[0] === 'string') {
						errors.push({ source: err.path[0], text: err.message });
					}
				});
			} else {
				rage.notifications.showException(error);
			}
		}

		setErrors(errors);
		dispatch(setLoading(false));
	};

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (errors !== null) {
				const { name, value } = event.target;
				if (name === 'password') {
					setPassword(value);
				} else if (name === 'passwordRep') {
					setPasswordRep(value);
				}
				setErrors(errors.filter((error) => error.source !== name));
			}
		},
		[errors],
	);

	const handleLoginClick = () => {
		dispatch(setActiveTab(AuthTabs.Login));
	};

	return (
		<PanelContainer>
			<FormTitle text={'Восстановление доступа к аккаунту'} />
			<FormContainer>
				<TextInput
					name="password"
					placeholder="Введите пароль"
					error={errors.find((error) => error.source === 'password')?.text}
					onChange={handleInputChange}
					tabIndex={1}
				/>
				<TextInput
					name="passwordRep"
					placeholder="Повторите пароль"
					error={errors.find((error) => error.source === 'passwordRep')?.text}
					onChange={handleInputChange}
					tabIndex={2}
				/>
			</FormContainer>
			<ButtonContainer>
				<Button
					primary
					name="restore"
					type="button"
					loading={loading}
					onClick={handleSubmit}
					tabIndex={3}
				>
					Сменить пароль
				</Button>
				<Button
					name="return"
					disabled={loading}
					onClick={handleLoginClick}
					tabIndex={4}
				>
					Вернуться назад
				</Button>
			</ButtonContainer>
		</PanelContainer>
	);
};

export { RestoreNewPassword };
